import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, ApiError } from '../api';
import { PageHeader } from '../components/PageHeader';

interface ConsentRequest {
  clientName: string;
  /** False for a client that registered itself and chose its own name, which is most of them. */
  clientVerified: boolean;
  scope: string;
  redirectUri: string;
  resource: string | null;
  expiresAt: string;
}

/**
 * Approving an AI agent's connection. This approval is what stands in for step-up MFA on every mint the
 * agent later makes, so it has to be explicit, has to name the client honestly, and has to say plainly
 * that a self-registered client's name proves nothing.
 */
export function Consent() {
  const [params] = useSearchParams();
  const id = params.get('request') ?? '';
  const [request, setRequest] = useState<ConsentRequest | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!id) {
      setError('That link is missing its request id.');
      return;
    }
    api<ConsentRequest>(`/api/consent/${encodeURIComponent(id)}`)
      .then(setRequest)
      .catch((err: unknown) => setError(err instanceof ApiError ? err.message : 'Something went wrong'));
  }, [id]);

  async function decide(decision: 'approve' | 'deny') {
    setBusy(true);
    setError(null);
    try {
      const { redirectTo } = await api<{ redirectTo: string }>(`/api/consent/${encodeURIComponent(id)}`, {
        method: 'POST',
        body: { decision },
      });
      // Back to the client that asked, carrying the code or the refusal.
      window.location.href = redirectTo;
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong');
      setBusy(false);
    }
  }

  if (error && !request) {
    return (
      <>
        <PageHeader title="Cannot continue" />
        <div className="card measure">
          <div className="alert">{error}</div>
          <p className="small muted" style={{ marginBottom: 0 }}>Nothing has been granted. You can close this window.</p>
        </div>
      </>
    );
  }
  if (!request) return <p className="muted">Loading…</p>;

  let host = request.redirectUri;
  try {
    host = new URL(request.redirectUri).host;
  } catch {
    /* shown verbatim if it will not parse */
  }

  return (
    <>
      <PageHeader title="Connect an AI agent" />
      <div className="card measure">
        <p className="lede">
          <strong>{request.clientName}</strong> wants to open Salesforce sessions as you.
        </p>

        {!request.clientVerified && (
          <div className="alert">
            This application registered itself and chose that name. It is not one we recognise. Only continue if you
            started this yourself, just now.
          </div>
        )}

        <div className="notice">
          <p style={{ margin: '0 0 8px' }}>If you approve, it will be able to:</p>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            <li>Mint short-lived Salesforce tokens for orgs where an admin has granted agent access</li>
            <li>Use those tokens directly against Salesforce, with whatever the granted user can do there</li>
          </ul>
          <p style={{ margin: '8px 0 0' }}>It cannot grant itself access to anything new, and every mint is audited against your name.</p>
        </div>

        <p className="small muted">
          Codes will be returned to <span className="mono">{host}</span>. This approval lasts 30 days, after which you
          will be asked again. You can end it sooner from your account page.
        </p>

        {error && <div className="alert">{error}</div>}
        {/* Equal blocks side by side, as on the device page: declining is a full answer, not the small print. */}
        <div className="decide">
          <button type="button" disabled={busy} onClick={() => decide('approve')}>
            Approve
          </button>
          <button type="button" className="secondary" disabled={busy} onClick={() => decide('deny')}>
            Decline
          </button>
        </div>
      </div>
    </>
  );
}
